import type { RouteLocationRaw } from 'vue-router';

import type { IconObject } from '@/dsComponents/buttons/types';
import type { WorkerMissionStatus } from '@/types/mission';

export enum FollowingSegmentType {
	TEXT = 'text',
	LINK = 'link',
	ACTION = 'action',
	READONLY = 'readonly',
	STATUS = 'status',
	DROPDOWN = 'dropdown',
}

export enum FollowingDropdownPopoverKind {
	INTERVIEW = 'interview',
	TEXT = 'text',
}

export enum FollowingDropdownTriggerMode {
	CLICK = 'click',
	HOVER = 'hover',
}

export interface FollowingBaseInput {
	date: number | string;
	detail?: string;
}

interface FollowingTextSegment {
	type: FollowingSegmentType.TEXT;
	content: string;
	multiline?: boolean;
}

interface FollowingLinkSegment {
	type: FollowingSegmentType.LINK;
	label: string;
	leftIcon?: string | IconObject;
	iconColor?: string;
	to: RouteLocationRaw;
}

interface FollowingActionSegment {
	type: FollowingSegmentType.ACTION;
	label: string;
	leftIcon?: string | IconObject;
	iconColor?: string;
	onClick: (event: MouseEvent) => void;
}

interface FollowingReadonlySegment {
	type: FollowingSegmentType.READONLY;
	label: string;
	leftIcon?: string | IconObject;
	iconColor?: string;
}

interface FollowingStatusSegment {
	type: FollowingSegmentType.STATUS;
	label: string;
	leftIcon?: string | IconObject;
	iconColor?: string;
}

export interface InterviewPopoverData {
	scheduledAt: string | number;
	scheduledEndAt?: string | number;
	madeBy: string;
	accentColor: string;
}

export type FollowingDropdownData =
	| {
			type: FollowingDropdownPopoverKind.INTERVIEW;
			payload: InterviewPopoverData;
	  }
	| { type: FollowingDropdownPopoverKind.TEXT; payload: string };

interface FollowingDropdownSegment {
	type: FollowingSegmentType.DROPDOWN;
	id?: string;
	label: string;
	leftIcon?: string | IconObject;
	iconColor?: string;
	mode?: FollowingDropdownTriggerMode;
	data?: FollowingDropdownData;
	width?: string;
}

export type FollowingSegment =
	| FollowingTextSegment
	| FollowingLinkSegment
	| FollowingActionSegment
	| FollowingReadonlySegment
	| FollowingStatusSegment
	| FollowingDropdownSegment;

export interface FollowingRightAction {
	label: string;
	onClick: (event: MouseEvent) => void;
}

export interface FollowingLineProps {
	icon?: string;
	segments?: FollowingSegment[];
	detail?: string;
	showThreadLine?: boolean;
	rightAction?: FollowingRightAction;
	onLinkNavigate?: (to: RouteLocationRaw, event: MouseEvent) => void;
}

export interface ContentBlockProps {
	content?: string;
	readOnlyExpandAction?: () => void;
	readOnlyExpandLabel?: string;
}

export interface FollowingNoteContext {
	noteId?: string;
	logCollaboratorId?: string | null;
}

export interface FollowingInterviewContext {
	interviewId: string;
}

export interface FollowingListItem extends FollowingLineProps {
	id: string;
	content?: string;
	noteContext?: FollowingNoteContext;
	interviewContext?: FollowingInterviewContext;
}

export interface FollowingListSectionProps {
	title: string;
	defaultOpen?: boolean;
	open?: boolean;
}

export type FollowingLineGeneratorResult = Omit<FollowingListItem, 'id'>;

export enum FollowingTypeDto {
	PREQUALIFIED = 'PREQUALIFIED',
	CONTACTED = 'CONTACTED',
	INTERVIEW = 'INTERVIEW',
	COMPANY_PROPOSED = 'COMPANY_PROPOSED',
	COMPANY_INTERVIEW = 'COMPANY_INTERVIEW',
	MISSION_PROPOSED = 'MISSION_PROPOSED',
	REJECTED = 'REJECTED',
}

export interface WorkerBesoinHistoryEntryDto {
	id: string;
	besoinId: string;
	besoinName: string;
	workerId: string;
	eventType: FollowingTypeDto | string;
	date: string;
	createdAt: string;
	userId: string;
	userFirstname: string;
	userLastname: string;
	agencyId?: string;
	/** Comment for CONTACTED or REJECTED events; otherwise null/absent. Read-only in the timeline. */
	comment?: string | null;
}

export interface MissionAddress {
	name?: string | null;
	street?: string | null;
	complement?: string | null;
	city: string;
	citySlug?: string;
	zip?: string | null;
	inseeCode?: string | null;
	countryCode: string;
	department?: string | null;
	region?: string | null;
	location?: [number, number] | null;
}

export interface WorkerActivityMissionCompany {
	name: string;
	id: string;
}

export interface WorkerActivityMissionItem {
	id: string;
	name: string;
	timestamp: string;
	companyName?: string;
	company?: WorkerActivityMissionCompany;
	companyId?: string | null;
	workerMissionStatus: WorkerMissionStatus;
	agencyId?: string;
	jobId?: string | null;
	jobName?: string;
	actorId?: string;
}

export enum ProfileHistorySource {
	WORKER = 'worker',
}

export interface ProfileHistoryPayloadAgency {
	name: string;
	_id: string;
}

export interface ProfileHistoryPayloadCollaborator {
	name?: string;
	_id?: string;
}

export interface ProfileHistoryPayloadContent {
	internalComment?: string;
	/** Present when the activity API exposes the worker note id for PATCH (legacy rows often omit it). */
	noteId?: string;
}

export interface ProfileHistoryLoggerPayload {
	type?: string;
	collectionOrigin?: string;
	agencyId?: string;
	agency_id?: string;
	agencyName?: string | null;
	publicName?: string | null;
	agency?: ProfileHistoryPayloadAgency;
	collaborator?: ProfileHistoryPayloadCollaborator;
	collaboratorName?: string | null;
	content?: string | ProfileHistoryPayloadContent | null;
	noteId?: string;
}

export interface ProfileHistoryItem {
	date: string;
	source: ProfileHistorySource | string;
	type: string;
	payload: ProfileHistoryLoggerPayload;
	collaboratorId: string | null;
	collaboratorName?: string | null;
	noteId?: string;
	agencyId?: string;
}

export const WORKER_APPLIED_TYPE = 'worker_applied';

export interface WorkerActivityJobOfferItem {
	id: string;
	jobOfferId: string;
	jobOfferName: string;
	workerId: string;
	besoinId: string;
	besoinName: string;
	applicationSource: string;
	type: typeof WORKER_APPLIED_TYPE;
	date: string;
}

export enum WorkerActivitySource {
	PROFILE_HISTORY = 'profileHistory',
	BESOIN = 'besoin',
	MISSIONS = 'missions',
	JOBOFFERS = 'jobOffers',
}

export type WorkerActivityItem =
	| {
			id: string;
			occurredAt: string;
			source: WorkerActivitySource.PROFILE_HISTORY;
			payload: ProfileHistoryItem;
	  }
	| {
			id: string;
			occurredAt: string;
			source: WorkerActivitySource.MISSIONS;
			payload: WorkerActivityMissionItem;
	  }
	| {
			id: string;
			occurredAt: string;
			source: WorkerActivitySource.BESOIN;
			payload: WorkerBesoinHistoryEntryDto;
	  }
	| {
			id: string;
			occurredAt: string;
			source: WorkerActivitySource.JOBOFFERS;
			payload: WorkerActivityJobOfferItem;
	  };

export interface WorkerActivityResponse {
	workerId: string;
	items: WorkerActivityItem[];
	nextCursor: string | null;
}
